import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

/**
 * The difference between a broken screen and a black one.
 *
 * React 18 unmounts the entire tree when a render throws and nothing catches
 * it. With no boundary anywhere, any error in any component leaves an empty
 * document — which the page background paints black — and the only trace is a
 * console line nobody outside a browser's developer tools will ever read. A rep
 * reports "the app is blank", and that sentence contains no information.
 *
 * This exists to convert that into a sentence somebody can act on.
 */

/**
 * A dynamic `import()` that could not be fetched.
 *
 * This is the failure a deployed single-page app hits most, and it is not a bug
 * in the code: the browser is holding an app shell that names chunks the server
 * has already replaced. The fix is a reload, and saying so is the whole
 * difference between a dead screen and a five-second recovery.
 *
 * Matched on the message because the browsers disagree on the type — Chrome
 * throws a TypeError, Firefox and Safari each word it differently — and there
 * is no error code to test.
 */
export function isStaleBuild(error: Error): boolean {
  const text = `${error.name}: ${error.message}`;
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|ChunkLoadError|Unable to preload/i
    .test(text);
}

interface Props {
  children: ReactNode;
  /** Named so the message can say *which* part failed when the boundary wraps
   *  a subtree rather than the whole app. */
  area?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // The component stack is the part that actually locates the fault, and it
    // is not on the error object.
    console.error('the interface crashed', error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    // A stale build is a different conversation from a bug: nothing is wrong
    // with the account, the data or the code, and a reload fixes it.
    if (isStaleBuild(error)) {
      return (
        <main className="centered">
          <div className="card">
            <h1>A new version is available</h1>
            <p>
              This page was loaded from an older build and part of it is no
              longer on the server. Reloading picks up the current one.
            </p>
            <button className="primary" onClick={() => window.location.reload()}>
              Reload
            </button>
          </div>
        </main>
      );
    }

    return (
      <main className="centered">
        <div className="card">
          <h1>Something went wrong</h1>
          <p className="muted">
            {this.props.area
              ? `The ${this.props.area} could not be displayed.`
              : 'This screen could not be displayed.'}
          </p>
          {/* The message is shown rather than hidden behind "contact support".
              Whoever is looking at this is usually the person who can fix it,
              and if they are not, it is what they will read out to somebody
              who can. */}
          <p className="error">{error.message || String(error)}</p>
          <button className="primary" onClick={() => window.location.reload()}>
            Reload
          </button>
          <p className="muted small">
            The full details, including which component failed, are in the
            browser’s developer console.
          </p>
        </div>
      </main>
    );
  }
}
